import type { GameId } from '@tira/protocol';
import { useApp } from '../state/store.js';
import { sfx, unlockSound, type SfxName } from './sfx.js';

/**
 * One listener, every game.
 *
 * Sounds are not sprinkled through six boards; they are read off the store the
 * same way the boards read the server. Every state the store publishes is a
 * server fact — a seq bump is a move, a receipt is a settlement — so a single
 * subscriber that diffs consecutive states can voice all six games without a
 * board knowing sound exists. The per-game part is only WHICH material a move
 * sounds like (a tansan is not a card is not a tile), plus a handful of
 * moments read out of the view payload by the tolerant peeks below.
 *
 * The view is `unknown` on purpose everywhere else, and stays that way here:
 * these peeks pull single fields out defensively and shrug when they are not
 * there, because a sound is the one consumer allowed to be wrong quietly.
 */

/* ── Tolerant view peeks ──────────────────────────────────────────────────── */

const obj = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;
const arr = (v: unknown): unknown[] | null => (Array.isArray(v) ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

/** Pieces on a dama board — a capture is this number going down. */
function damaPieces(view: unknown): number | null {
  const b = arr(obj(view)?.['board']);
  return b === null ? null : b.filter((c) => c !== null).length;
}

/** Cards across all tongits melds — up means a bahay laid or a sapaw landed. */
function tongitsMeldCards(view: unknown): number | null {
  const m = arr(obj(view)?.['melds']);
  if (m === null) return null;
  let n = 0;
  for (const meld of m) {
    const cards = arr(obj(meld)?.['cards']);
    if (cards) n += cards.length;
  }
  return n;
}

function mahjong(view: unknown): { river: number; melds: number; claim: boolean } | null {
  const o = obj(view);
  if (!o) return null;
  return {
    river: arr(o['river'])?.length ?? 0,
    melds: arr(o['melds'])?.length ?? 0,
    claim: obj(o['claim']) !== null,
  };
}

function geraClashes(view: unknown): number | null {
  return arr(obj(view)?.['clashes'])?.length ?? null;
}

/* ── The pure part: what changed, as sounds ───────────────────────────────── */

/** The slice of the store the director reads. Structural, for testability. */
export interface SoundView {
  match: {
    matchId: string;
    game: GameId;
    seat: number;
    seq: number;
    turn: number | null;
    view: unknown;
  } | null;
  receipt: { lines: { userId: string; delta: number }[] } | null;
  me: { userId: string } | null;
  table: { seats: unknown[] } | null;
  talk: { at: number }[];
  toast: { tone: string } | null;
}

/** Each venue's move lands in its own material. */
const MATERIAL: Record<GameId, SfxName> = {
  dama: 'tansan',
  tongits: 'card',
  pusoydos: 'card',
  pusoy: 'card',
  mahjong: 'tile',
  gera: 'standee',
};

/**
 * Diff two consecutive store states into the sounds they earn, in play order.
 * Pure — the module's tests live entirely on this function.
 */
export function transitions(prev: SoundView, next: SoundView): SfxName[] {
  const out: SfxName[] = [];
  const p = prev.match;
  const n = next.match;

  /* A move landed: same hand, later seq. The material is the venue's own. */
  if (p && n && n.matchId === p.matchId && n.seq > p.seq) {
    let voiced = false;

    /* Moments that outrank the plain move, read from the payload. */
    if (n.game === 'dama') {
      const a = damaPieces(p.view);
      const b = damaPieces(n.view);
      if (a !== null && b !== null && b < a) {
        out.push('capture');
        voiced = true;
      }
    } else if (n.game === 'gera') {
      const a = geraClashes(p.view);
      const b = geraClashes(n.view);
      if (a !== null && b !== null && b > a) {
        out.push('capture');
        voiced = true;
      }
    } else if (n.game === 'mahjong') {
      const a = mahjong(p.view);
      const b = mahjong(n.view);
      if (a && b) {
        if (b.melds > a.melds) {
          // A claim resolving into a laid meld is the takeaway of the game.
          out.push('capture');
          voiced = true;
        } else if (!a.claim && b.claim) {
          out.push('claim');
          voiced = true;
        }
      }
    } else if (n.game === 'tongits') {
      const a = tongitsMeldCards(p.view);
      const b = tongitsMeldCards(n.view);
      if (a !== null && b !== null && b > a) {
        out.push('capture'); // a bahay laid or a sapaw landing on one
        voiced = true;
      }
    }

    if (!voiced) out.push(MATERIAL[n.game]);
  }

  /* The clock came to me. After the move sound: the move is why it is mine. */
  if (n && n.seat >= 0 && n.turn === n.seat && (!p || p.matchId !== n.matchId || p.turn !== n.turn)) {
    out.push('turn');
  }

  /* Settlement: one sting keyed to my own line, then the chips counting out. */
  if (next.receipt && next.receipt !== prev.receipt) {
    const mine = next.me
      ? next.receipt.lines.find((l) => l.userId === next.me?.userId)
      : undefined;
    out.push(mine === undefined ? 'chips' : mine.delta > 0 ? 'win' : mine.delta < 0 ? 'lose' : 'chips');
    if (mine !== undefined && mine.delta !== 0) out.push('chips');
  }

  /* Somebody sat down at my table. */
  if (prev.table && next.table && prev.table !== next.table) {
    const before = prev.table.seats.filter((s) => s !== null).length;
    const after = next.table.seats.filter((s) => s !== null).length;
    if (after > before) out.push('seat');
  }

  /* A table-talk line landed. */
  const lastPrev = prev.talk[prev.talk.length - 1]?.at ?? 0;
  const lastNext = next.talk[next.talk.length - 1]?.at ?? 0;
  if (lastNext > lastPrev) out.push('talk');

  /* A warning toast — the server said no. Wins already sting elsewhere. */
  if (next.toast && next.toast !== prev.toast && next.toast.tone === 'warn') out.push('clock');

  return out;
}

/* ── The impure shell ─────────────────────────────────────────────────────── */

const pick = (s: ReturnType<typeof useApp.getState>): SoundView => ({
  match: s.match,
  receipt: s.receipt,
  me: s.me,
  table: s.table,
  talk: s.talk,
  toast: s.toast,
});

let started = false;

/**
 * Mounted once from the app root. Also owns the autoplay unlock: the first
 * pointer anywhere resumes the context, which is the only moment the browser
 * allows it.
 */
export function startSoundDirector(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  window.addEventListener('pointerdown', unlockSound, { passive: true });

  let prev = pick(useApp.getState());
  useApp.subscribe((state) => {
    const next = pick(state);
    const names = transitions(prev, next);
    if (import.meta.env.DEV && names.length > 0) {
      ((window as unknown as { __sfxLog?: string[] }).__sfxLog ??= []).push(...names);
    }
    for (const name of names) sfx(name);
    prev = next;
  });
}
